import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, message, Spin, Empty, DatePicker } from "antd";
const { RangePicker } = DatePicker;
import { EnvironmentOutlined, DashboardOutlined, AimOutlined, ScanOutlined } from "@ant-design/icons";
import { Map, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  ComposedChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import InfraredCamera from "../../api/InfraredCamera";
import InfraredCalibrationModal from "../modal/Infrared/InfraredCalibrationModal";

// ─── Leaflet icon 修正 ───────────────────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const deviceIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const stationIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});
const selectedStationIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
});

// ─── 圖表 Tooltip 客製化 ─────────────────────────────────────────────────────
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const maxEntry = payload.find((p) => p.dataKey === "temp_max");
  const minEntry = payload.find((p) => p.dataKey === "temp_min");
  const diff = maxEntry?.value != null && minEntry?.value != null
    ? parseFloat((maxEntry.value - minEntry.value).toFixed(2))
    : null;
  return (
    <div style={{ background: "var(--content-background)", border: "1px solid var(--border-color)", padding: "8px 12px", borderRadius: 6, fontSize: 12 }}>
      <div style={{ marginBottom: 4, color: "var(--secondary-text)" }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: p.color }}>
          {p.name}：{p.value != null ? p.value : "—"}
          {["temperature", "camera_temp_1", "camera_temp_2", "camera_temp_3", "temp_max", "temp_min"].includes(p.dataKey) ? "°C" : p.dataKey === "humidity" ? "%" : ""}
        </div>
      ))}
      {diff != null && (
        <div style={{ color: "var(--secondary-text)", marginTop: 4, borderTop: "1px solid var(--border-color)", paddingTop: 4 }}>
          溫差：{diff}°C
        </div>
      )}
    </div>
  );
};

// ─── 時間格式化（只顯示 MM/DD HH:00）────────────────────────────────────────
const formatTime = (t) => {
  if (!t) return "";
  const d = new Date(t);
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:00`;
};

export default function Function15({ machineID, data, status, setStatus, machineInfo }) {
  // ─── Form ─────────────────────────────────────────────────────────────────
  const [form] = Form.useForm();

  // ─── Modal 開關 ───────────────────────────────────────────────────────────
  const [coordinateModal, setCoordinateModal] = useState(false);
  const [chartModal, setChartModal]           = useState(false);
  const [stationModal, setStationModal]       = useState(false);
  const [calibModal, setCalibModal]           = useState(false);

  // ─── 載入狀態 ─────────────────────────────────────────────────────────────
  const [coordLoading,    setCoordLoading]    = useState(false);
  const [stationLoading,  setStationLoading]  = useState(false);
  const [chartLoading,    setChartLoading]    = useState(false);
  const [selectingStation, setSelectingStation] = useState(null);

  // ─── 資料 ─────────────────────────────────────────────────────────────────
  const [nearbyStations, setNearbyStations] = useState([]);
  const [chartData,      setChartData]      = useState(null);
  const [chartRange,     setChartRange]     = useState('72h');
  const [customRange,    setCustomRange]    = useState(null);
  const [localData,      setLocalData]      = useState(null);
  const [fetchedData,    setFetchedData]    = useState(null);
  const currentData = localData || fetchedData || data;

  // ─── 頁面載入時主動 fetch 最新資料（含即時溫濕度）────────────────────────
  useEffect(() => {
    if (!machineID) return;
    InfraredCamera.GetData(machineID).then(res => {
      if (res.code === 200) setFetchedData(res.data);
    }).catch(() => {});
  }, [machineID, status]);

  // ─── 座標 form 預填 ───────────────────────────────────────────────────────
  useEffect(() => {
    if (data) {
      form.setFieldsValue({ latitude: data.latitude, longitude: data.longitude, camera_sn: data.camera_sn });
      setLocalData(null);
    }
  }, [data, form]);

  // ─── 圖表資料共用 fetch（快捷 range 或自訂 start/end 二擇一）────────────
  const fetchChartData = async ({ range, start, end }) => {
    setChartLoading(true);
    try {
      const res = await InfraredCamera.GetChartData(machineID, { range, start, end });
      if (res.code === 200) setChartData(res.data);
    } catch (err) {
      message.error(err.uiMessage || "載入圖表資料失敗");
    } finally {
      setChartLoading(false);
    }
  };

  // ─── 開啟圖表 Modal ───────────────────────────────────────────────────────
  const handleOpenChartModal = async () => {
    setChartModal(true);
    if (customRange) {
      await fetchChartData({ start: customRange[0].toISOString(), end: customRange[1].toISOString() });
    } else {
      await fetchChartData({ range: chartRange });
    }
  };

  // ─── 快捷按鈕切換 ─────────────────────────────────────────────────────────
  const handleChangeRange = async (range) => {
    setChartRange(range);
    setCustomRange(null);
    await fetchChartData({ range });
  };

  // ─── 自訂時間區間 ─────────────────────────────────────────────────────────
  const handleCustomRange = async (dates) => {
    if (!dates) {
      setCustomRange(null);
      await fetchChartData({ range: chartRange });
      return;
    }
    setCustomRange(dates);
    setChartRange(null);
    await fetchChartData({ start: dates[0].toISOString(), end: dates[1].toISOString() });
  };

  // ─── 將歷史資料格式化為圖表 series ───────────────────────────────────────
  const buildChartSeries = () => {
    if (!chartData) return [];

    const bandMap = {};
    (chartData.temp_bands || []).forEach((b) => {
      const ts = new Date(b.bucket).getTime();
      const key = Math.floor(ts / 21600000) * 21600000;
      bandMap[key] = {
        temp_max: b.temp_max != null ? parseFloat(Number(b.temp_max).toFixed(2)) : null,
        temp_min: b.temp_min != null ? parseFloat(Number(b.temp_min).toFixed(2)) : null,
      };
    });

    return (chartData.history || []).map((h) => {
      const ts  = new Date(h.time).getTime();
      const key = Math.floor(ts / 21600000) * 21600000;
      const band = bandMap[key] || {};
      return {
        time:         formatTime(h.time),
        humidity:     h.humidity     != null ? parseFloat(Number(h.humidity).toFixed(2))     : null,
        camera_temp_1: h.camera_temp_1 != null ? parseFloat(Number(h.camera_temp_1).toFixed(2)) : null,
        camera_temp_2: h.camera_temp_2 != null ? parseFloat(Number(h.camera_temp_2).toFixed(2)) : null,
        camera_temp_3: h.camera_temp_3 != null ? parseFloat(Number(h.camera_temp_3).toFixed(2)) : null,
        temp_max:     band.temp_max ?? null,
        temp_min:     band.temp_min ?? null,
      };
    });
  };

  // ─── 開啟選站地圖 ─────────────────────────────────────────────────────────
  const handleOpenStationModal = async () => {
    setNearbyStations([]);
    setStationModal(true);
    setStationLoading(true);
    try {
      const res = await InfraredCamera.GetNearbyStations(machineID, 30);
      if (res.code === 200) setNearbyStations(res.data);
    } catch (err) {
      message.error(err.uiMessage || "載入氣象站失敗");
    } finally {
      setStationLoading(false);
    }
  };

  // ─── 選擇氣象站 ───────────────────────────────────────────────────────────
  const handleSelectStation = async (stationId) => {
    setSelectingStation(stationId);
    try {
      await InfraredCamera.SelectStation(machineID, stationId);
      message.success("已選擇氣象站");
      setNearbyStations((prev) => prev.map((s) => ({ ...s, is_selected: s.id === stationId })));
      const picked = nearbyStations.find((s) => s.id === stationId);
      setLocalData((prev) => ({
        ...(prev || data),
        selected_station_id:  stationId,
        station_name:         picked?.station_name,
        weather_temperature:  picked?.temperature ?? null,
        weather_humidity:     picked?.humidity    ?? null,
      }));
    } catch (err) {
      message.error(err.uiMessage || "選站失敗");
    } finally {
      setSelectingStation(null);
    }
  };

  // ─── 更新參數設定 ─────────────────────────────────────────────────────────
  const handleUpdateCoordinate = async (values) => {
    setCoordLoading(true);
    try {
      await InfraredCamera.UpdateSettings(machineID, values);
      message.success("參數更新成功");
      setCoordinateModal(false);
      setLocalData((prev) => ({ ...(prev || data), ...values }));
      setStatus((prev) => !prev);
    } catch (err) {
      message.error(err.uiMessage || "參數更新失敗");
    } finally {
      setCoordLoading(false);
    }
  };

  // ─── 地圖中心點 ───────────────────────────────────────────────────────────
  const devLat = parseFloat(currentData?.latitude) || 25.020329;
  const devLon = parseFloat(currentData?.longitude) || 121.529085;

  const chartSeries   = buildChartSeries();
  const hasHistory    = chartSeries.length > 0;
  const xAxisIntervalMap = { "24h": 2, "72h": 4, "7d": 11, "30d": 47 };
  const xAxisInterval = customRange
    ? Math.max(1, Math.round((customRange[1] - customRange[0]) / 3600000 / 12))
    : (xAxisIntervalMap[chartRange] ?? 4);

  return (
    <React.Fragment>
      <td className="ant-table-cell" style={{ textAlign: "center" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "center" }}>

          {/* 機具編號 */}
          <div style={{ fontSize: "12px", color: "var(--primary-text)", fontWeight: 500 }}>
            機具編號：{machineID}
          </div>

          {/* 氣象站 */}
          {currentData?.station_name && (
            <div style={{ fontSize: "12px", color: "var(--primary-text)" }}>
              氣象站：{currentData.station_name}
              {currentData.station_distance != null && ` (${Number(currentData.station_distance).toFixed(2)}km)`}
            </div>
          )}

          {/* 溫濕度 */}
          {(currentData?.weather_temperature != null || currentData?.weather_humidity != null) && (
            <div style={{ fontSize: "12px", color: "var(--secondary-text)" }}>
              {currentData.weather_temperature != null && `🌡 ${Number(currentData.weather_temperature).toFixed(2)}°C`}
              {currentData.weather_temperature != null && currentData.weather_humidity != null && "  "}
              {currentData.weather_humidity != null && `💧 ${Number(currentData.weather_humidity).toFixed(2)}%`}
            </div>
          )}

          {(currentData?.camera_temp_1 != null || currentData?.camera_temp_2 != null || currentData?.camera_temp_3 != null) && (
            <div style={{ fontSize: "12px", color: "var(--secondary-text)", marginTop: "2px" }}>
              🔥 {[1, 2, 3].map(i => {
                const v = currentData[`camera_temp_${i}`];
                return `實測${i}: ${v != null ? Number(v).toFixed(1) + "°C" : "----"}`;
              }).join("　")}
            </div>
          )}

          {/* 按鈕 */}
          <div style={{ display: "flex", gap: "6px", marginTop: "4px" }}>
            <Button icon={<EnvironmentOutlined />} onClick={() => setCoordinateModal(true)} type="primary" size="small">設定</Button>
            <Button icon={<AimOutlined />} onClick={handleOpenStationModal} size="small">選站</Button>
            <Button icon={<DashboardOutlined />} onClick={handleOpenChartModal} size="small">圖表</Button>
            <Button icon={<ScanOutlined />} onClick={() => setCalibModal(true)} size="small">標定</Button>
          </div>
        </div>

        {/* ── MSX 對位標定 Modal ──────────────────────────────────────────── */}
        <InfraredCalibrationModal
          open={calibModal}
          onClose={() => setCalibModal(false)}
          machineID={machineID}
        />

        {/* ── 參數設定 Modal ──────────────────────────────────────────────── */}
        <Modal
          title="參數設定"
          open={coordinateModal}
          onOk={() => form.submit()}
          onCancel={() => setCoordinateModal(false)}
          confirmLoading={coordLoading}
        >
          <Form form={form} onFinish={handleUpdateCoordinate} layout="vertical">
            <Form.Item label="緯度" name="latitude" rules={[
              { required: true, message: "請輸入緯度" },
              { pattern: /^-?([0-8]?[0-9]|90)(\.\d+)?$/, message: "請輸入有效的緯度(-90 ~ 90)" },
            ]}>
              <Input placeholder="例如: 25.020329" />
            </Form.Item>
            <Form.Item label="經度" name="longitude" rules={[
              { required: true, message: "請輸入經度" },
              { pattern: /^-?((1[0-7][0-9])|([0-9]?[0-9]))(\.\d+)?$/, message: "請輸入有效的經度(-180 ~ 180)" },
            ]}>
              <Input placeholder="例如: 121.529085" />
            </Form.Item>
            <Form.Item label="鏡頭 SN 號" name="camera_sn" rules={[
              { required: true, message: "請輸入鏡頭 SN 號" },
            ]}>
              <Input placeholder="例如: BH0F12BPAG4940C" />
            </Form.Item>
          </Form>
        </Modal>

        {/* ── 選站地圖 Modal ──────────────────────────────────────────────── */}
        <Modal
          title="選擇氣象站"
          open={stationModal}
          onCancel={() => setStationModal(false)}
          footer={null}
          width={700}
          bodyStyle={{ padding: "12px" }}
        >
          {stationLoading ? (
            <div style={{ textAlign: "center", padding: "40px" }}><Spin tip="載入氣象站中..." /></div>
          ) : (
            <>
              <div style={{ height: 380, marginBottom: 12, borderRadius: 6, overflow: "hidden" }}>
                <Map center={[devLat, devLon]} zoom={13} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
                  <Marker position={[devLat, devLon]} icon={deviceIcon}>
                    <Popup>📷 紅外線鏡頭位置</Popup>
                  </Marker>
                  {nearbyStations.map((s) => (
                    <Marker key={s.id} position={[s.latitude, s.longitude]} icon={s.is_selected ? selectedStationIcon : stationIcon}>
                      <Popup>
                        <div style={{ minWidth: 140 }}>
                          <div style={{ fontWeight: "bold", marginBottom: 4 }}>{s.station_name}</div>
                          <div style={{ fontSize: 12, color: "#595959", marginBottom: 8 }}>距離 {s.distance} km</div>
                          <Button type={s.is_selected ? "default" : "primary"} size="small" block
                            loading={selectingStation === s.id} disabled={s.is_selected}
                            onClick={() => handleSelectStation(s.id)}>
                            {s.is_selected ? "已選擇" : "選擇此站"}
                          </Button>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </Map>
              </div>
              <div style={{ fontSize: 12, color: "var(--secondary-text)" }}>
                🔴 鏡頭位置　🔵 氣象站　🟢 已選擇的站　點擊標記可選擇氣象站
              </div>
            </>
          )}
        </Modal>

        {/* ── 數據圖表 Modal ──────────────────────────────────────────────── */}
        <Modal
          title={`氣象數據圖表${chartData ? ` — ${chartData.station_id?.split("_")[2] || ""}` : ""}`}
          open={chartModal}
          onCancel={() => setChartModal(false)}
          footer={null}
          width={860}
          bodyStyle={{ padding: "16px" }}
        >
          {chartLoading ? (
            <div style={{ textAlign: "center", padding: "60px" }}><Spin tip="載入資料中..." /></div>
          ) : !hasHistory ? (
            <Empty description="尚無資料，等待下次氣象站同步（每小時更新）" />
          ) : (
            <>
              {/* 時間選擇器 */}
              <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", flexWrap: "wrap" }}>
                {[
                  { key: '24h', label: '24h' },
                  { key: '72h', label: '72h' },
                  { key: '7d',  label: '7d'  },
                  { key: '30d', label: '30d' },
                ].map((opt) => (
                  <Button
                    key={opt.key}
                    size="small"
                    type={chartRange === opt.key ? 'primary' : 'default'}
                    onClick={() => handleChangeRange(opt.key)}
                  >
                    {opt.label}
                  </Button>
                ))}
                <RangePicker
                  size="small"
                  showTime={{ format: "HH:00" }}
                  format="MM/DD HH:00"
                  value={customRange}
                  onChange={handleCustomRange}
                  allowClear
                  placeholder={["開始時間", "結束時間"]}
                  style={{ marginLeft: 4 }}
                />
              </div>

              {/* 溫度折線圖 */}
              <div style={{ marginBottom: 8, fontWeight: 500, color: "var(--primary-text)" }}>溫度 (°C)</div>
              <ResponsiveContainer width="100%" height={220}>
                <ComposedChart data={chartSeries} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={xAxisInterval} />
                  <YAxis tick={{ fontSize: 11 }} unit="°C" domain={["auto", "auto"]} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend iconSize={12} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="camera_temp_1" name="實測溫度 1" stroke="#389e0d" dot={false} strokeWidth={2} connectNulls />
                  <Line type="monotone" dataKey="camera_temp_2" name="實測溫度 2" stroke="#d4b106" dot={false} strokeWidth={2} connectNulls />
                  <Line type="monotone" dataKey="camera_temp_3" name="實測溫度 3" stroke="#d46b08" dot={false} strokeWidth={2} connectNulls />
                  <Line type="monotone" dataKey="temp_max" name="最高溫" stroke="#ff4d4f" dot={false} strokeWidth={1.5} strokeDasharray="5 4" connectNulls />
                  <Line type="monotone" dataKey="temp_min" name="最低溫" stroke="#4096ff" dot={false} strokeWidth={1.5} strokeDasharray="5 4" connectNulls />
                </ComposedChart>
              </ResponsiveContainer>

              {/* 濕度折線圖 */}
              <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 500, color: "var(--primary-text)" }}>濕度 (%)</div>
              <ResponsiveContainer width="100%" height={180}>
                <ComposedChart data={chartSeries} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="time" tick={{ fontSize: 10 }} interval={xAxisInterval} />
                  <YAxis tick={{ fontSize: 11 }} unit="%" domain={[0, 100]} />
                  <Tooltip content={<ChartTooltip />} />
                  <Legend iconSize={12} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="humidity" name="濕度" stroke="#1890ff" dot={false} strokeWidth={2} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>

              <div style={{ fontSize: 11, color: "var(--secondary-text)", marginTop: 8, textAlign: "right" }}>
                氣象站：{chartData?.station_id?.split("_")[2]}　每小時更新
              </div>
            </>
          )}
        </Modal>
      </td>
    </React.Fragment>
  );
}